import {useEffect, useState, useRef, RefObject} from "react";
import {get, debounce} from "lodash";

interface DOMRectReadOnly {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

interface ResizeObserverCallback {
  (entries: ResizeObserverEntry[], observer: ResizeObserver): void;
}

interface ResizeObserverEntry {
  readonly target: Element;
  readonly contentRect: DOMRectReadOnly;
}

interface ResizeObserver {
  observe(target: Element): void;
  unobserve(target: Element): void;
  disconnect(): void;
}

declare global {
  interface Window {
    ResizeObserver?: {
      prototype: ResizeObserver;
      new (callback: ResizeObserverCallback): ResizeObserver;
    };
  }
}

// https://developers.google.com/web/fundamentals/performance/rail
export const WINDOW_RESIZE_WAIT = 50;

export function useOverflowObserver<TRef extends HTMLElement>(
  isOpen: boolean
): [RefObject<TRef>, boolean] {
  const ref = useRef<TRef>(null);
  const [isOverflowed, setIsOverflowed] = useState(false);
  const resizeObserverLib = useRef<Window["ResizeObserver"]>();

  useEffect(() => {
    async function initResizeObserver(): Promise<void> {
      if ("ResizeObserver" in window) {
        resizeObserverLib.current = window.ResizeObserver;
      } else {
        const ResizeObserver = (await import("resize-observer-polyfill"))
          .default;
        resizeObserverLib.current = ResizeObserver;
      }
    }
    initResizeObserver();
  }, []);

  useEffect(() => {
    const handleResize = (): void =>
      setIsOverflowed(
        get(ref, "current.clientHeight", 0) >= window.innerHeight
      );

    const handleResizeDebounced = debounce(handleResize, WINDOW_RESIZE_WAIT);

    const element = ref.current;

    if (!element || !resizeObserverLib.current) {
      return undefined;
    }
    window.addEventListener("resize", handleResizeDebounced);

    const ResizeObserver = resizeObserverLib.current;

    const resizeObserver = new ResizeObserver((entries) => {
      window.requestAnimationFrame(() => {
        if (entries) handleResize();
      });
    });

    resizeObserver.observe(element);

    return () => {
      window.removeEventListener("resize", handleResizeDebounced);
      resizeObserver.disconnect();
    };
  }, [isOpen, resizeObserverLib.current]);

  return [ref, isOverflowed];
}
